import { defineStore, acceptHMRUpdate } from 'pinia';
import { signOut } from '@/utils/auth';
import { useWebSockets } from '@/stores/useWebSockets';
import { getUserInitials, getUsername, formatUserRoles } from '@/utils/user';
import { sort } from '@/utils/sort';
import { userApi } from '@/services/userService';
import { clearNavState } from '@/composables/useNavigation';

const SYSTEM_USER = 'sys';

export const useUsersStore = defineStore('users', {
  state: () => ({
    users: [],
    notFound: false,
    statusLookup: [],
    activeUser: null,
    status: null,
    shiftDuration: 8,
    usersLoading: false,
  }),
  getters: {
    /**
     * Returns the full name of a user with the given username.
     */
    getUserFullName: state => {
      return username => {
        if (!username) return null;
        const user = state.users.find(
          user => user?.username?.toLowerCase() === username.toLowerCase()
        );

        if (username === SYSTEM_USER) return 'System User';
        return user?.full_name;
      };
    },
    getUserFullNameFromModifier: state => {
      return (modByUserId, modBy) => {
        if (modByUserId != null && modByUserId !== '') {
          const byId = state.users.find(
            u =>
              u.auxo_user_id === modByUserId || u.user_id === modByUserId
          );
          if (byId?.full_name) return byId.full_name;
        }
        if (!modBy) return null;
        if (modBy === SYSTEM_USER) return 'System User';
        const user = state.users.find(
          u => u?.username?.toLowerCase() === modBy.toLowerCase()
        );
        // Prefer directory full name; if user not loaded or service/seed actor, show mod_by
        return user?.full_name ?? modBy;
      };
    },
    getGroupOfUsersNames: state => usersArray => {
      if (!state.users.length) return;

      return usersArray.map(e => {
        return {
          name: `${e.first_name} ${e.last_name}`,
          is_active: e.is_active,
        };
      });
    },
    getUsernames: state => {
      return state.users.map(user => {
        return {
          key: user.username,
          value: `${user.first_name} ${user.last_name}`,
        };
      });
    },
    getUserById: state => {
      return userId => {
        const user = state.users.find(
          u => u.user_id === userId || u.auxo_user_id === userId
        );
        return {
          ...user,
          initials: getUserInitials(user),
        };
      };
    },
    getActiveUsersRoles: state => {
      return state.activeUser['cognito:groups'];
    },
    isActiveUserAdmin: state => {
      return state.activeUser['cognito:groups'].some(
        userRole => userRole === 'ADM'
      );
    },
    activeUsers: state => {
      if (state.usersLoading == false) {
        return state.users.filter(user => user.is_active);
      }
    },
    getUserByUsername: state => {
      return username => {
        if (!username) return null;
        const user = state.users.find(
          user => user.username.toLowerCase() === username.toLowerCase()
        );
        return {
          ...user,
          initials: getUserInitials(user),
        };
      };
    },
    getUserByFullName: state => {
      return fullName => {
        if (!fullName) return null;
        const user = state.users.find(
          user => user.full_name.toLowerCase() === fullName.toLowerCase()
        );
        if (!user) return null;
        return {
          ...user,
          initials: getUserInitials(user),
        };
      };
    },
    getUserInitials: state => {
      return username => {
        if (!username) return null;

        const user = state.users.find(
          user => user.username.toLowerCase() === username.toLowerCase()
        );
        return user ? getUserInitials(user) : null;
      };
    },
    getUserPicture: state => {
      return username => {
        if (!username) return null;

        return (
          state.users.find(
            user => user.username.toLowerCase() === username.toLowerCase()
          )?.photo_url || null
        );
      };
    },
    sortUsers: state => {
      return (order, field) => {
        return sort(order, field, state.users);
      };
    },
    getActiveUserAttribute: state => {
      return attr => {
        return state.activeUser[attr];
      };
    },
  },
  actions: {
    setStatus(status) {
      this.status = status;
      localStorage.setItem('userStatus', this.status?.name);
      return status;
    },
    async getAllUsers() {
      this.usersLoading = true;
      const users = await userApi.getAllUsers();

      if (users) {
        this.users = users
          .map(user => {
            user.photo_url = user.photo_url || 'none';
            const full_name = [user?.first_name, user?.last_name]
              .filter(e => e)
              .join(' ');

            return {
              ...user,
              username: getUsername(user.email),
              full_name,
              informal_title: formatUserRoles(user?.user_roles),
              label: full_name,
            };
          })
          .sort((a, b) => {
            return a.first_name.localeCompare(b.first_name, 'en', {
              sensitivity: 'accent',
            });
          });
      }
      this.usersLoading = false;
    },
    async getUserByEmail(email) {
      if (!email) return;
      // TODO: when cognito returns auxo_user_id remove this and call /users/<auxo_user_id>

      this.notFound = false;
      const user = await userApi.getUserByEmail(email);
      if (!user) {
        this.notFound = true;
        console.error(`User email ${email} not found`);
        return null;
      }
      return {
        ...user,
        username: getUsername(user.email),
      };
    },
    async getUser(auxo_user_id) {
      this.notFound = false;
      this.isLoading = true;
      const user = await userApi.getUserById(auxo_user_id);
      if (!user) {
        this.notFound = true;
      }
      this.isLoading = false;
      return user
        ? {
            ...user,
            username: getUsername(user.email),
          }
        : null;
    },
    async setUserGoal(auxo_user_id, goal) {
      this.isLoading = true;
      await userApi.setUserGoal(auxo_user_id, goal);

      if (this.activeUser && this.activeUser.auxo_user_id === auxo_user_id) {
        this.activeUser.daily_goal = goal;
      }

      this.users = this.users.map(user =>
        user.auxo_user_id === auxo_user_id ? { ...user, user_goal: goal } : user
      );

      this.isLoading = false;
    },
    async logOut() {
      // Await the close so the WebSocket sends a proper close frame (code 1000)
      // before signOut() navigates the page away. This ensures $disconnect fires
      // on the backend and the ws_connection row is properly marked as disconnected.
      try {
        await useWebSockets().close();
      } catch (err) {
        console.warn(
          'WebSocket close failed during logout, proceeding anyway.',
          err
        );
      }

      sessionStorage.removeItem('activityDrafts');
      clearNavState();

      try {
        await this.authActivity('logout');
      } catch (err) {
        console.error('authActivity failed during logout:', err);
      } finally {
        if (import.meta.env.VITE_LOCAL_AUTH === 'true' && import.meta.env.MODE === 'desk') {
          const { localSignOut } = await import('@/utils/localAuth');
          await localSignOut();
        } else {
          await signOut();
        }
      }
    },
    async authActivity(action) {
      const token = sessionStorage.getItem('id_token');
      if (!token || token === 'undefined') {
        console.warn(
          'authActivity skipped: no active session token or invalid token value.'
        );
        if (token === 'undefined') {
          sessionStorage.removeItem('id_token');
        }
        return;
      }

      const res = await userApi.authActivity(action);
      if (action === 'login' && res?.data?.[0]?.daily_login_time) {
        this.activeUser.daily_login_time = res.data[0].daily_login_time;
      }
      return res;
    },
    async setUserShiftDuration(auxo_user_id, shift_duration) {
      this.isLoading = true;
      const response = await userApi.setUserShiftDuration(
        auxo_user_id,
        shift_duration
      );

      this.activeUserID == auxo_user_id
        ? (this.activeUserDailyGoal = response?.data?.data)
        : (this.selectedUserDailyGoal = response?.data?.data);

      if (this.activeUser && this.activeUser.auxo_user_id === auxo_user_id) {
        this.activeUser.shift_duration = shift_duration;
      }
      this.users = this.users.map(user =>
        user.auxo_user_id === auxo_user_id
          ? { ...user, shift_duration: shift_duration }
          : user
      );

      this.isLoading = false;
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUsersStore, import.meta.hot));
}
